import { useSyncExternalStore } from "react";
import { pushWorldPref } from "../prefs.js";
import {
  isWeatherSetting,
  parseWeatherIntent,
  settledMix,
  stateOf,
  weatherFromQuery,
} from "./weather.js";
import type { WeatherMix, WeatherSetting, WeatherState } from "./weather.js";

/**
 * The user's choice of weather (`world.weather`): `?weather=` pins it (screenshots, tests),
 * otherwise the device cache and the server's copy once the prefs arrive. Same shape as the time
 * of day store (`timeStore.ts`). The live mix the scene draws is `weatherNow` (WeatherDriver).
 */
export const WEATHER_STORAGE_KEY = "uz.world.weather";

let pinnedByUrl = false;
let setting: WeatherSetting = "auto";
const listeners = new Set<() => void>();

function readCache(): WeatherSetting | null {
  try {
    const raw = globalThis.localStorage.getItem(WEATHER_STORAGE_KEY);
    return isWeatherSetting(raw) ? raw : null;
  } catch {
    return null;
  }
}

function writeCache(next: WeatherSetting): void {
  try {
    globalThis.localStorage.setItem(WEATHER_STORAGE_KEY, next);
  } catch {
    // Not cached without storage.
  }
}

/** The mix the scene shows right now (written by the driver every frame). */
export const weatherNow: { mix: WeatherMix; state: WeatherState } = {
  mix: settledMix("clear"),
  state: "clear",
};

export function initWeatherSetting(): void {
  const q = weatherFromQuery();
  pinnedByUrl = q !== null;
  setting = q ?? readCache() ?? "auto";
  // A pinned or cached state shows settled from the first frame (no fade-in on load).
  const state = stateOf(setting, Date.now() / 1000);
  weatherNow.mix = settledMix(state);
  weatherNow.state = state;
}
initWeatherSetting();

export function getWeatherSetting(): WeatherSetting {
  return setting;
}

export function subscribeWeather(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function emit(next: WeatherSetting): void {
  if (next === setting) return;
  setting = next;
  for (const l of listeners) l();
}

/** The user (settings, assistant command) picks a weather: applied now, cached, saved. */
export function setWeatherSetting(next: WeatherSetting): void {
  pinnedByUrl = false;
  emit(next);
  writeCache(next);
  pushWorldPref({ weather: next });
}

/** A value read from the server: applied and cached, never echoed back (and `?weather=` wins). */
export function adoptWeatherSetting(next: WeatherSetting | undefined): void {
  if (next === undefined || pinnedByUrl) return;
  emit(next);
  writeCache(next);
}

/** For `syncWorldPrefs`: what this device has cached, to push up when the server has none. */
export function cachedWeatherSetting(): WeatherSetting | undefined {
  return pinnedByUrl ? undefined : (readCache() ?? undefined);
}

export function useWeatherSetting(): WeatherSetting {
  return useSyncExternalStore(subscribeWeather, getWeatherSetting, () => "auto");
}

/** A typed or spoken weather command: applies it and returns the setting, or null. */
export function applyWeatherCommand(text: string): WeatherSetting | null {
  const next = parseWeatherIntent(text);
  if (next !== null) setWeatherSetting(next);
  return next;
}

/** The world-namespace toast key that confirms a weather command. */
export function weatherToast(next: WeatherSetting): {
  key: string;
  params: Record<string, string>;
} {
  return { key: `weather.set_${next}`, params: {} };
}

/** How much sun gets through the weather (scales the sun or moon light, 0.25 .. 1). */
export function sunScale(): number {
  const w = weatherNow.mix;
  const c = Math.min(1, Math.max(0, (w.cloud - 0.3) / 0.7));
  return Math.max(0.25, 1 - 0.7 * c * c * (3 - 2 * c) - 0.3 * w.fog);
}
